import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  LinearProgress,
  Paper,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import {
  ShipmentLineType,
  ShipmentLinesUploadError,
  ShipmentLinesUploadJobStatus,
  ShipmentLinesUploadResult,
} from "../../../../../models/Shipments/ShipmentInventoryLines";
import { useStores } from "../../../use-stores";

const MAX_ROWS = 5000;
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const JOB_POLL_MS = 3000;

interface LineTypeConfig {
  label: string;
  templateFileName: string;
  templateCsv: string;
  /** Aliases are compared lowercased with spaces removed, like the backend. */
  requiredColumns: { label: string; aliases: string[] }[];
  columnGuide: { name: string; note: string }[];
  duplicateRule: string;
}

const SHIPMENT_ID_COLUMN = {
  label: "Shipment ID",
  aliases: ["shipmentid", "shipment_id"],
};
const QUANTITY_COLUMN = { label: "Quantity", aliases: ["quantity", "qty"] };

const LINE_TYPE_CONFIG: Record<ShipmentLineType, LineTypeConfig> = {
  PACK: {
    label: "Packs",
    templateFileName: "Shipment_Pack_Lines_Template.csv",
    templateCsv: [
      "Shipment ID,Pack ID,Quantity",
      "5001,41,1",
      "5002,41,1",
      "5002,42,2",
    ].join("\n"),
    requiredColumns: [
      SHIPMENT_ID_COLUMN,
      { label: "Pack ID", aliases: ["packid", "pack_id"] },
      QUANTITY_COLUMN,
    ],
    columnGuide: [
      { name: "Shipment ID", note: "Positive integer" },
      { name: "Pack ID", note: "Positive integer, pack in the same inventory" },
      { name: "Quantity", note: "Number of packs (positive integer)" },
    ],
    duplicateRule:
      "A shipment + pack pair can appear only once. One shipment can have several packs.",
  },
  ITEM: {
    label: "Items",
    templateFileName: "Shipment_Item_Lines_Template.csv",
    templateCsv: [
      "Shipment ID,SKU,Quantity",
      "5001,TOTE-NAT,2",
      "5002,TOTE-NAT,1",
      "5002,CAP-ONE,1",
    ].join("\n"),
    requiredColumns: [
      SHIPMENT_ID_COLUMN,
      { label: "SKU", aliases: ["sku", "clientsku", "client_sku"] },
      QUANTITY_COLUMN,
    ],
    columnGuide: [
      { name: "Shipment ID", note: "Positive integer" },
      { name: "SKU", note: "Active SKU in the shipments' inventory" },
      { name: "Quantity", note: "Number of units (positive integer)" },
    ],
    duplicateRule:
      "A shipment + SKU pair can appear only once (SKU is not case-sensitive). One shipment can have several SKUs.",
  },
};

const LINE_TYPES: ShipmentLineType[] = ["PACK", "ITEM"];

/** The job this popup follows: one it queued, or one already running for the order. */
type TrackedJob =
  | ({ kind: "queued" } & ShipmentLinesUploadResult)
  | { kind: "alreadyRunning"; jobId: number };

interface ShipmentLinesCsvUploadPopupProps {
  open: boolean;
  onClose: () => void;
  /** Called once a job finished writing lines, so the listing can reload. */
  onUpdated: () => void;
}

const validateCsvFile = async (
  file: File,
  config: LineTypeConfig,
): Promise<string | null> => {
  if (!file.name.toLowerCase().endsWith(".csv")) {
    return "Please upload a CSV file.";
  }
  if (file.size > MAX_FILE_BYTES) {
    return "CSV must be 2MB or smaller.";
  }
  const lines = (await file.text())
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "");
  if (lines.length < 2) {
    return "CSV has no shipment rows.";
  }
  if (lines.length - 1 > MAX_ROWS) {
    return `CSV has ${lines.length - 1} rows. The limit is ${MAX_ROWS}.`;
  }
  const headers = lines[0]
    .replace(/^﻿/, "")
    .split(",")
    .map((header) =>
      header.trim().replace(/^"|"$/g, "").toLowerCase().replace(/\s+/g, ""),
    );
  const missing = config.requiredColumns
    .filter(
      (column) => !column.aliases.some((alias) => headers.includes(alias)),
    )
    .map((column) => column.label);
  if (missing.length > 0) {
    return `Missing column(s): ${missing.join(", ")}. Download the ${config.label.toLowerCase()} template for the expected format.`;
  }
  return null;
};

const ShipmentLinesCsvUploadPopup = observer(
  ({ open, onClose, onUpdated }: ShipmentLinesCsvUploadPopupProps) => {
    const theme = useTheme();
    const { shipmentStore } = useStores();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const jobTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onUpdatedRef = useRef(onUpdated);
    onUpdatedRef.current = onUpdated;

    const [lineType, setLineType] = useState<ShipmentLineType>("PACK");
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [queued, setQueued] = useState<TrackedJob | null>(null);
    const [job, setJob] = useState<ShipmentLinesUploadJobStatus | null>(null);

    const config = LINE_TYPE_CONFIG[lineType];
    const isJobRunning = Boolean(queued) && !job?.finished;
    const isBusy = uploading || isJobRunning;

    useEffect(
      () => () => {
        if (jobTimerRef.current) {
          clearTimeout(jobTimerRef.current);
        }
      },
      [],
    );

    // A running background job keeps polling while closed; reopening shows its progress.
    useEffect(() => {
      if (!open || isJobRunning) return;
      setCsvFile(null);
      setFileError("");
      setUploadError("");
      setQueued(null);
      setJob(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const pollJob = (jobId: number) => {
      jobTimerRef.current = setTimeout(async () => {
        jobTimerRef.current = null;
        try {
          const next = await shipmentStore.getShipmentLinesUploadJob(jobId);
          setJob(next);
          if (!next.finished) {
            pollJob(jobId);
          } else {
            // Shipments replaced before an unexpected stop stay replaced, so reload either way.
            onUpdatedRef.current();
            void shipmentStore.fetchRunningLinesUploadJobs();
          }
        } catch (error: unknown) {
          setUploadError(
            error instanceof Error
              ? error.message
              : "Failed to fetch lines upload job.",
          );
        }
      }, JOB_POLL_MS);
    };

    const handleLineTypeChange = (next: ShipmentLineType) => {
      if (isBusy || next === lineType) return;
      setLineType(next);
      setCsvFile(null);
      setFileError("");
      setUploadError("");
      setQueued(null);
      setJob(null);
    };

    const handleDownloadTemplate = () => {
      try {
        const blob = new Blob([config.templateCsv], {
          type: "text/csv;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = config.templateFileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch {
        setFileError("Failed to download the template. Please try again.");
      }
    };

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file = event.target.files?.[0] || null;
      event.target.value = "";
      if (!file || isBusy) return;
      setQueued(null);
      setJob(null);
      setUploadError("");
      try {
        const error = await validateCsvFile(file, config);
        setCsvFile(error ? null : file);
        setFileError(error || "");
      } catch {
        setCsvFile(null);
        setFileError("Failed to read the CSV file.");
      }
    };

    const handleRemoveFile = () => {
      if (uploading) return;
      setCsvFile(null);
      setFileError("");
    };

    const handleUpload = async () => {
      if (!csvFile || isBusy) return;
      setUploading(true);
      setUploadError("");
      setQueued(null);
      setJob(null);
      try {
        const result = await shipmentStore.uploadShipmentLinesCsv(
          lineType,
          csvFile,
        );
        setQueued({ kind: "queued", ...result });
        setCsvFile(null);
        pollJob(result.jobId);
        void shipmentStore.fetchRunningLinesUploadJobs();
      } catch (error: unknown) {
        // Another upload for this order is running: follow it so the button stays disabled.
        if (
          error instanceof ShipmentLinesUploadError &&
          error.runningJobId != null
        ) {
          setQueued({ kind: "alreadyRunning", jobId: error.runningJobId });
          pollJob(error.runningJobId);
        }
        setUploadError(
          error instanceof Error
            ? error.message
            : `Failed to upload ${config.label.toLowerCase()} CSV.`,
        );
      } finally {
        setUploading(false);
      }
    };

    const handleClose = () => {
      if (uploading) return;
      onClose();
    };

    const sectionLabel = (text: string) => (
      <CustomTypography variant="body2" fontWeight={600}>
        {text}
      </CustomTypography>
    );

    const renderOutcome = () => {
      if (!queued) return null;

      const finished = Boolean(job?.finished);
      const failed = finished && !job?.success;
      const summary =
        job?.summary ||
        (queued.kind === "queued"
          ? `Replacing lines on ${queued.shipmentCount} shipment${
              queued.shipmentCount === 1 ? "" : "s"
            } (${queued.queuedRows} of ${queued.totalRows} row${
              queued.totalRows === 1 ? "" : "s"
            }) in the background.`
          : "Another upload for this order is running. Wait for it to finish before uploading again.");

      return (
        <Box sx={{ mt: 1.5 }}>
          <CustomTypography
            variant="body2"
            fontWeight={600}
            sx={{
              color: failed
                ? theme.palette.error.main
                : finished
                  ? theme.palette.customColors.successGreen
                  : undefined,
            }}
          >
            {failed
              ? "Upload stopped on an error. Shipments replaced before it stay replaced."
              : finished
                ? "Upload finished."
                : `Job ${queued.jobId} running…`}
          </CustomTypography>
          {!finished && (
            <LinearProgress
              variant={job ? "determinate" : "indeterminate"}
              value={job?.progress ?? 0}
              sx={{ mt: 1, borderRadius: 4, height: 6 }}
            />
          )}
          <CustomTypography
            variant="body3"
            color="text.secondary"
            sx={{ mt: 0.75, display: "block" }}
          >
            {summary}
          </CustomTypography>
          {queued.kind === "queued" && (
            <CustomTypography
              variant="body3"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Inventory {queued.inventoryId}
              {queued.orderId != null ? ` · Order ${queued.orderId}` : ""}
              {` · ${queued.shipmentsByLineStatus?.RESERVED ?? 0} Reserved (New / Ready To Ship)`}
              {` · ${queued.shipmentsByLineStatus?.DISPATCHED ?? 0} Dispatched (Shipped)`}
            </CustomTypography>
          )}
          {queued.kind === "queued" && (queued.skipped ?? []).length > 0 && (
            <Box sx={{ mt: 1 }}>
              <CustomTypography
                variant="body3"
                fontWeight={600}
                sx={{ color: theme.palette.warning.main }}
              >
                Skipped {queued.skippedCount} row
                {queued.skippedCount === 1 ? "" : "s"} (not updated):
              </CustomTypography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  mt: 0.5,
                  pl: 2.5,
                  maxHeight: 160,
                  overflowY: "auto",
                }}
              >
                {queued.skipped.map((row) => (
                  <li key={`${row.rowNumber}-${row.shipmentId}`}>
                    <CustomTypography variant="body3" color="text.secondary">
                      Row {row.rowNumber}, shipment {row.shipmentId}:{" "}
                      {row.reason}
                    </CustomTypography>
                  </li>
                ))}
              </Box>
            </Box>
          )}
          {!finished && (
            <CustomTypography
              variant="body3"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              You can close this window. The listing reloads when the job
              finishes.
            </CustomTypography>
          )}
        </Box>
      );
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          paper: {
            sx: {
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
              position: "relative",
            },
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            // Flex column so the content scrolls and the header / actions stay visible.
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
            },
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <Box minWidth={0}>
              <CustomTypography variant="heading4" fontWeight={700}>
                Bulk Upload Packs / Items
              </CustomTypography>
              <CustomTypography variant="body3" color="text.secondary">
                Replace the packs and items on many shipments from a CSV.
              </CustomTypography>
            </Box>
            <IconButton
              onClick={handleClose}
              disabled={uploading}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: 3, minHeight: 0, overflowY: "auto" }}>
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                  {sectionLabel("UPLOAD")}
                  <Box
                    sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    {LINE_TYPES.map((type) => (
                      <AppButton
                        key={type}
                        label={LINE_TYPE_CONFIG[type].label}
                        variant={type === lineType ? "primary" : "grayscale"}
                        onClick={() => handleLineTypeChange(type)}
                        disabled={isBusy && type !== lineType}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid2>

              <Grid2 size={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box minWidth={0}>
                      {sectionLabel("CSV TEMPLATE")}
                      <CustomTypography variant="body3" color="text.secondary">
                        Max {MAX_ROWS} rows, 2MB.
                      </CustomTypography>
                    </Box>
                    <AppButton
                      label="Download Template"
                      variant="secondary"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadTemplate}
                      disabled={uploading}
                    />
                  </Box>
                  <Grid2 container spacing={1} sx={{ mt: 1.5 }}>
                    {config.columnGuide.map((column) => (
                      <Grid2 key={column.name} size={{ xs: 12, sm: 4 }}>
                        <CustomTypography variant="body3" fontWeight={600}>
                          {column.name}
                        </CustomTypography>
                        <CustomTypography
                          variant="body3"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {column.note}
                        </CustomTypography>
                      </Grid2>
                    ))}
                  </Grid2>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.error.main}`,
                    }}
                  >
                    <CustomTypography
                      variant="body3"
                      fontWeight={600}
                      sx={{ color: theme.palette.error.main }}
                    >
                      This replaces existing lines
                    </CustomTypography>
                    <CustomTypography
                      variant="body3"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      For every shipment in the file, all existing packs and
                      items are removed first, then the file&apos;s rows are
                      added. A {config.label.toLowerCase()} CSV also removes{" "}
                      {lineType === "PACK" ? "item" : "pack"} lines. Shipments
                      not in the file are not touched.
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.warning.main}`,
                    }}
                  >
                    <CustomTypography variant="body3" fontWeight={600}>
                      All shipments must be from the same order. Only New, Ready
                      To Ship and Shipped shipments are updated
                    </CustomTypography>
                    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                      {[
                        "New / Ready To Ship: new lines are Reserved.",
                        "Shipped: old lines go back to stock, then new lines are Dispatched and stock is deducted. Needs the permission to edit items on shipped shipments. Skipped if stock is short.",
                        "Other shipments (Delivered, Cancelled, Returned, not found) are skipped and listed after upload. The rest are still updated.",
                        "All shipments in the file must also share the same inventory.",
                        config.duplicateRule,
                        "Bad values, duplicate rows, or more than one order or inventory reject the whole file. A shipment that changes before it is written is skipped and keeps its old lines.",
                        "Only one upload per order can run at a time.",
                      ].map((rule) => (
                        <li key={rule}>
                          <CustomTypography
                            variant="body3"
                            color="text.secondary"
                          >
                            {rule}
                          </CustomTypography>
                        </li>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </Grid2>

              <Grid2 size={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                  {sectionLabel("CSV FILE")}
                  <CustomTypography variant="body3" color="text.secondary">
                    Upload the filled {config.label.toLowerCase()} template
                  </CustomTypography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={(event) => {
                      void handleFileChange(event);
                    }}
                  />
                  <Box
                    sx={{
                      mt: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <AppButton
                      label={csvFile ? "Replace CSV" : "Choose CSV"}
                      variant="grayscale"
                      startIcon={<UploadFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isBusy}
                    />
                    {csvFile && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        minWidth={0}
                      >
                        <CustomTypography
                          variant="body2"
                          fontWeight={600}
                          sx={{ wordBreak: "break-all" }}
                        >
                          {csvFile.name}
                        </CustomTypography>
                        <IconButton
                          size="small"
                          onClick={handleRemoveFile}
                          disabled={uploading}
                          aria-label="Remove CSV file"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <InlineFormError message={fileError} sx={{ mt: 1.5 }} />
                  {uploadError && (
                    <Box sx={{ mt: 1.5, maxHeight: 240, overflowY: "auto" }}>
                      <InlineFormError
                        message={uploadError}
                        sx={{ whiteSpace: "pre-line" }}
                      />
                    </Box>
                  )}
                  {renderOutcome()}
                </Paper>
              </Grid2>
            </Grid2>
          </DialogContent>

          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexShrink: 0,
              borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <AppButton
              label={queued ? "Done" : "Cancel"}
              onClick={handleClose}
              variant="grayscale"
              disabled={uploading}
            />
            <AppButton
              label={uploading ? "Uploading…" : "Upload CSV"}
              onClick={() => {
                void handleUpload();
              }}
              variant="primary"
              disabled={isBusy || !csvFile}
            />
          </Box>
        </Box>
      </Dialog>
    );
  },
);

export default ShipmentLinesCsvUploadPopup;
